<template>
    <v-row class="white-card" align="center" dense>
        <v-col :cols="isStudent ? 12 : 10">
            <kimsa-combobox outlined dense solo :items="exerciseOptions" @change="exercise = $event" :value="exercise"
                placeholder-class no-object item-value="objectID" />
        </v-col>
        <v-col cols="2" v-if="!isStudent">
            <v-btn @click="$refs.selector.show()" fab icon small color="accent" class="mb-1">
                <v-icon>mdi-cog-outline</v-icon>
            </v-btn>
            <exercises-selector-statistics-dialog :coach-id="coachId" ref="selector" :limit="$t('objectives.strong')" @saved="loadExercises" />
        </v-col>
        <v-col cols="12" align="right">
            <group-by-selector @change="currentGroupBy = $event" :value="currentGroupBy" />
        </v-col>
        <v-col cols="12">
            <line-chart :labels="labels" :datasets="datasets" style="height: 280px" ref="chart" />
        </v-col>
    </v-row>
</template>

<script>
import GroupBySelector from "@/components/statistics/GroupBySelector";
import LineChart from "@/components/common/LineChart";
import _groupBy from "lodash/groupBy";
import KimsaCombobox from '../common/inputs/KimsaCombobox.vue';
import ExercisesSelectorStatisticsDialog from "@/components/coach/ExercisesSelectorStatisticsDialog";
import { db } from "@/utils/Firebase";
import { ExerciseIndex } from "@/utils/Algolia";

export default {
    name: "ExercisesChart",
    components: { LineChart, GroupBySelector, KimsaCombobox, ExercisesSelectorStatisticsDialog },
    props: {
        docs: {
            type: Array,
            default: null,
        },
        coachId: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            currentGroupBy: 'daily',
            exerciseOptions: [],
            exercise: [],
            routines: [],
            datasets: [],
            labels: [],
            lineTemplate: {
                borderColor: '#078571',
                pointBackgroundColor: 'rgba(255,255,255, 0.5)',
                pointRadius: 6,
                pointHoverRadius: 6,
                pointBorderWidth: 3,
                borderWidth: 2,
                tension: 0,
            },
        }
    },
    computed: {
        isStudent(vm = this) {
            return vm.account?.accountType === 'student'
        },
        groupRoutines(vm = this) {
            let routines = vm.routines.map(routine => {
                let date = vm.$moment(routine.date)
                if (vm.currentGroupBy === 'weekly') date = date.startOf('week')
                if (vm.currentGroupBy === 'monthly') date = date.startOf('month')

                return {
                    ...routine,
                    date: date,
                    dateString: date.format('DD MMM').replace('.', ''),
                }
            })

            if (vm.currentGroupBy !== 'daily') {
                let grouped = _groupBy(routines, d => {
                    return d.dateString
                })

                routines = []
                for (let currentDate in grouped) {
                    if (grouped[currentDate].length === 1) {
                        routines.push(grouped[currentDate][0]);
                    } else {
                        let accumulatedValues = {};
                        grouped[currentDate].forEach(group => {
                            for (let key in group) {
                                if (key !== 'date' && key !== 'dateString') {
                                    accumulatedValues[key] = (accumulatedValues[key] || 0) + group[key];
                                }
                            }
                        });
                        routines.push({
                            date: grouped[currentDate][0].date,
                            dateString: grouped[currentDate][0].dateString,
                            ...accumulatedValues
                        });
                    }
                }
            }

            return routines
        },
    },
    async mounted() {
        const vm = this
        await vm.loadExercises()
        vm.loadStats()
        vm.loadChart()
    },
    methods: {
        async loadExercises() {
            let vm = this
            if (!vm.coachId) return;
            vm.exerciseOptions = []
            let coachRef = await db.collection('users').doc(vm.coachId).get()
            let coachExercises = coachRef.data().statistics_exercises || null

            let filter = `objectID:"${coachExercises.join('" OR objectID:"')}"`
            // console.log('searching by:', filter)
            ExerciseIndex.search('', {
                filters: filter,
                hitsPerPage: 10,
            })
                .then(({ hits }) => {
                    // console.log('hits', hits)
                    vm.exerciseOptions = hits
                    vm.exercise = vm.exerciseOptions[0]?.objectID || null
                })
        },
        loadStats() {
            const vm = this;
            const routines = vm.docs.map(doc => {
                let sets = 0
                let weight = 0
                let quantity_strong = 0
                doc?.blocks?.forEach(block => {
                    block.exercises.forEach(exercise => {
                        if (exercise.type !== 'rest' && exercise.objectID === vm.exercise) {
                            exercise.config?.sets?.forEach((set, i) => {
                                if (exercise.categories?.[0] === vm.$t('objectives.strong')) {
                                    sets += Number(exercise.config?.sets?.length || 0)
                                    weight += vm.takeValueFromSet(set, 'weight')
                                    quantity_strong++
                                }
                            })
                        }
                    })
                })

                if (quantity_strong > 0) weight /= quantity_strong
                weight = Number(Number(weight).toFixed(1).replace('.0', ''))

                return {
                    date: doc.rawDate.full,
                    sets: sets,
                    weight: weight,
                }
            })
            vm.routines = routines.filter(routine => routine.sets || routine.weight)
        },
        loadChart() {
            const vm = this

            let gradientFill = this.$refs.chart.$refs.canvas.getContext('2d').createLinearGradient(0, 50, 0, 250);
            gradientFill.addColorStop(0, "#D7FFAF");
            gradientFill.addColorStop(1, "rgba(204, 255, 153, 0)");

            vm.labels = vm.groupRoutines.map(routine => routine.dateString)

            let datasets = [
                {
                    label: vm.$tc('exercises.sets', 2),
                    data: vm.groupRoutines.map(routine => routine.sets),
                    backgroundColor: gradientFill,
                    type: 'line',
                    yAxisID: 'left-axis',
                    ...vm.lineTemplate,
                },
                {
                    label: vm.$t('exercises.weight'),
                    data: vm.groupRoutines.map(routine => routine.weight),
                    ...vm.lineTemplate,
                    type: 'bar',
                    yAxisID: 'right-axis',
                },
            ]

            vm.datasets = datasets
        },
        takeValueFromSet(set, attribute) {
            const vm = this
            let defaultValue = 0
            let value = set.data.find(data => data.name === vm.$t(`exercises.${attribute}`))
            return !!value || value === 0 ? Number(value.value || 0) : defaultValue
        },
    },
    watch: {
        groupRoutines() {
            this.loadChart()
        },
        exercise() {
            this.loadStats()
            this.loadChart()
        },
        coachId() {
            this.loadExercises()
        },
        docs() {
            this.loadStats()
        }
    }
}
</script>

<style scoped></style>